<template>
  <div class="file-drag-drop-condition">
    <div
      :class="dragDropClasses"
      @dragover.prevent
      @dragenter.prevent="onDragEnter"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <form ref="fileform" class="fileform">
        <div class="fileform-content" @click="triggerInputEvent">
          <label for="fileFieldHandle" class="drop-files"> <slot></slot> </label><br />
          <input
            class="fileFieldHandle"
            ref="fileFieldHandle"
            type="file"
            onclick="value = null"
            accept="application/pdf"
            @click="resetFileUploader"
            @change="handleFileUpload"
          />
        </div>
      </form>
    </div>
    <Loader :isVisible="loadShow" :loadingText="loadText" />
    <!-- <button @click="testClicked">Testing</button>  -->
  </div>
</template>

<script>
/* eslint-disable */
import Loader from '@/components/Loader.vue';
import axios from 'axios';

export default {
  components: {
    Loader,
  },
  data() {
    return {
      file: '',
      embedSrc: '',
      info: null,
      loadShow: false,
      loadText: '',
      isDragging: false,
      dragCounter: 0,
    };
  },
  mounted() {},
  computed: {
    dragDropClasses() {
      return {
        'file-drag-drop': true,
        'drag-over': this.isDragging,
      };
    },
  },
  methods: {
    triggerInputEvent() {
      this.$refs.fileFieldHandle.click();
    },
    resetFileUploader() {
      this.$refs.fileFieldHandle.value = '';
    },
    async handleFileUpload(event) {
      const files = event.target.files;

      const isValid = await this.validateFile(files);
      if (!isValid) {
        this.resetFileUploader();
        return;
      }
      this.file = event.target.files[0];
      this.embedSrc = window.URL.createObjectURL(this.file);

      if (this.$listeners.fileSelected) {
        this.$emit('fileSelected', this.file);
        return;
      }

      this.uploadFile();
    },
    onDragEnter() {
      this.dragCounter += 1; // refer https://stackoverflow.com/a/21002544
      this.isDragging = true;
    },
    onDragLeave() {
      this.dragCounter -= 1;
      if (this.dragCounter === 0) {
        // Only remove the drag state when counter reaches zero
        this.isDragging = false;
      }
    },
    async onDrop(event) {
      event.preventDefault();
      this.dragCounter = 0;
      this.isDragging = false;
      const files = event.dataTransfer.files;

      const isValid = await this.validateFile(files);
      if (!isValid) {
        this.resetFileUploader();
        return;
      }

      this.file = files[0];
      this.embedSrc = URL.createObjectURL(this.file);

      if (this.$listeners.fileSelected) {
        this.$emit('fileSelected', this.file);
        return;
      }

      this.uploadFile();
    },
    uploadFile(fileToUpload) {
      if (fileToUpload) {
        this.file = fileToUpload;
        this.embedSrc = URL.createObjectURL(this.file);
      }
      let formData = new FormData();
      formData.append('file', this.file);

      const progressMessages = [
        'Processing document',
        'Reading pages and layout',
        'Identifying document type',
        'Extracting header information',
        'Extracting shipment details',
        'Validating extracted data',
        'Preparing job creation data',
        'Finalizing results',
      ];

      const idleMessages = [
        'Populating data',
        'Matching with existing records',
        'Performing final checks',
        'Almost done',
      ];

      let loadingIndex = 0;
      let isIdlePhase = false;
      let loadingStopped = false;
      let loadingTimeout = null;

      const runLoading = () => {
        if (loadingStopped) return;

        if (!isIdlePhase) {
          if (loadingIndex < progressMessages.length - 1) {
            loadingIndex++;
            this.loadText = progressMessages[loadingIndex];

            const delay = Math.min(2500 + loadingIndex * 1500, 7000);
            loadingTimeout = setTimeout(runLoading, delay);
          } else {
            isIdlePhase = true;
            loadingIndex = 0;
            this.loadText = idleMessages[loadingIndex];
            loadingTimeout = setTimeout(runLoading, 2200);
          }
        } else {
          loadingIndex = (loadingIndex + 1) % idleMessages.length;
          this.loadText = idleMessages[loadingIndex];
          loadingTimeout = setTimeout(runLoading, 2200);
        }
      };

      // start loading
      this.loadShow = true;
      this.loadText = progressMessages[0];
      loadingTimeout = setTimeout(runLoading, 1200);

      axios
        .post('/CustomExtractor/ProcessFile', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then((response) => {
          loadingStopped = true;
          if (loadingTimeout) clearTimeout(loadingTimeout);
          this.loadShow = false;
          this.loadText = '';
          this.info = response;
          this.$emit('input', {
            file: this.file,
            data: this.info,
            fileName: this.file.name,
          });
        })
        .catch((error) => {
          loadingStopped = true;
          if (loadingTimeout) clearTimeout(loadingTimeout);
          this.loadShow = false;
          this.loadText = '';

          let errMsg = 'Unknown error occurred';

          if (error.response) {
            if (typeof error.response.data === 'string') {
              errMsg = error.response.data;
            } else if (error.response.data?.error?.message) {
              errMsg = error.response.data.error.message;
            } else {
              errMsg = JSON.stringify(error.response.data);
            }
          } else if (error.request) {
            errMsg = 'No response from server';
          } else {
            errMsg = error.message;
          }

          //console.log('Full error:', error);

          this.$showAlert('error', 'An Error Occurred', errMsg);
        });
    },
    validateFile: async function (files) {
      // console.log(files)
      // validate single file only
      if (files.length > 1) {
        this.$showAlert('error', 'Multiple Files Detected', 'Please select or drop only one file.');
        return false;
      }

      // validate pdf file by MIME type
      const file = files[0];
      if (file.type !== 'application/pdf') {
        this.$showAlert('error', 'Invalid File Type', 'Please select or drop a PDF file.');
        return false;
      }

      // validate by file name
      const fileExtension = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();

      if (fileExtension !== '.pdf') {
        this.$showAlert('error', 'Invalid File Extension', 'Please select or drop a file with a .pdf extension.');
        return false;
      }

      // validate the file content
      const isValidPdf = await this.isPdfContentValid(file);
      if (!isValidPdf) {
        this.$showAlert('error', 'Invalid File Content', 'The file content does not appear to be a valid PDF.');
        return false;
      }

      return true;
    },
    isPdfContentValid: function (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (event) => {
          const arrayBuffer = event.target.result;
          const uintArray = new Uint8Array(arrayBuffer);
          const header = uintArray.subarray(0, 4); // Get the first 4 bytes
          const headerString = Array.from(header)
            .map((byte) => String.fromCharCode(byte))
            .join('');

          if (headerString === '%PDF') {
            resolve(true); // The file is a valid PDF
          } else {
            resolve(false); // The file is not a valid PDF
          }
        };

        reader.onerror = () => {
          reject(false);
        };

        reader.readAsArrayBuffer(file); // Read the file as binary data
      });
    },
    testClicked() {
      const fakeFileContent = 'This is the content of the fake file.';
      const blob = new Blob([fakeFileContent], { type: 'application/pdf' });
      const fakeFile = new File([blob], 'fakefile.pdf', { type: 'application/pdf' });

      this.file = fakeFile;

      this.embedSrc = window.URL.createObjectURL(this.file);
      let formData = new FormData();
      formData.append('file', this.file);
      this.loadShow = true;
      this.loadText = 'Processing Document';
      axios
        .post('/CustomExtractorTEST/ProcessFile', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then((response) => {
          this.loadShow = false;
          this.loadText = '';
          this.info = response;
          this.$emit('input', {
            file: this.file,
            data: this.info,
          });
        })
        .catch((error) => {
          //console.log(error.message);
          this.loadShow = false;
          this.loadText = '';
          this.$showAlert('error', 'An Error Occurred', `Please refresh or try again later`);
        });
    },
  },
};
</script>

<style scoped>
.file-drag-drop-condition {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 0;
}
.file-drag-drop {
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  height: 350px;
  width: 650px;
  border: 1px solid #28262c;
  border-radius: 15px;
  cursor: pointer;
  box-sizing: border-box;
  margin-left: 0;
}

.fileform {
  display: flex;
  align-items: center;
  justify-content: center;
}

.formDisplayColumn {
  width: 50%;
}

.fileform-content {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 600px;
  height: 300px;
  transition: background-color 0.5s ease;
  border: 2px dashed #28262c;
  border-radius: 10px;
}

.drop-files {
  font-size: 18px;
  text-align: center;
}

.fileFieldHandle {
  display: none;
}

.file-drag-drop:hover {
  box-shadow:
    0 4px 8px 0 rgba(0, 0, 0, 0.2),
    0 6px 20px 0 rgba(0, 0, 0, 0.19);
}

.file-drag-drop:hover > .fileform > .fileform-content {
  background: #b3c4ff;
  border: unset;
  color: black;
}

.drag-over {
  background: #e0e7ff;
  border: 2px dashed #5a67d8;
  transform: scale(1.02);
}
</style>
